import { rgbToRgba } from "./constants";
import { resolveServiceCategoryNameFromLabel } from "./templateToServiceCategory";

function normalizeRfiName(name) {
  return String(name || "").trim();
}

function normalizeRfiNameLower(name) {
  return normalizeRfiName(name).toLowerCase();
}

/**
 * Find an RFI catalog row by id or display name.
 * Name lookup is case-insensitive and tolerates a trailing "New" suffix mismatch.
 */
export function findRfiCatalogEntry(input, rfisList) {
  if (!input || !rfisList?.length) return null;

  if (typeof input === "object" && input.rfi_id != null) {
    const byId = rfisList.find((r) => String(r.rfi_id) === String(input.rfi_id));
    if (byId) return byId;
  }

  const nameCandidate =
    typeof input === "object"
      ? input.rfi_name || input.name || input.label
      : typeof input === "string"
        ? input
        : null;

  if (nameCandidate) {
    const trimmed = normalizeRfiName(nameCandidate);
    if (trimmed) {
      const candidates = [trimmed];
      const resolved = resolveServiceCategoryNameFromLabel(trimmed);
      if (resolved && resolved !== trimmed) candidates.push(resolved);

      for (const candidate of candidates) {
        const lower = normalizeRfiNameLower(candidate);
        let match = rfisList.find((r) => normalizeRfiNameLower(r.rfi_name) === lower);
        if (match) return match;

        const withoutNew = candidate.replace(/New$/i, "").trim();
        if (withoutNew !== candidate) {
          const wnLower = withoutNew.toLowerCase();
          match = rfisList.find((r) => normalizeRfiNameLower(r.rfi_name) === wnLower);
          if (match) return match;
          match = rfisList.find(
            (r) =>
              normalizeRfiName(r.rfi_name).replace(/New$/i, "").trim().toLowerCase() === wnLower
          );
          if (match) return match;
        }

        const withNew = `${candidate}New`;
        match = rfisList.find((r) => normalizeRfiNameLower(r.rfi_name) === withNew.toLowerCase());
        if (match) return match;
      }
    }
  }

  const idCandidate =
    typeof input === "object" ? input.value ?? input.rfi_id : input;
  if (idCandidate != null && String(idCandidate).trim() !== "") {
    return rfisList.find((r) => String(r.rfi_id) === String(idCandidate)) || null;
  }

  return null;
}

export function findRfiColorMeta(input, rfisList) {
  const match = findRfiCatalogEntry(input, rfisList);
  if (!match) return null;
  return {
    bg: match.rfi_color_code_bg,
    text: match.rfi_color_code_text,
    border: match.rfi_color_code_border,
  };
}

/** Same color resolution as Capabilities / Drawer — { bgColor, textColor, borderColor } or null. */
export function resolveRfiBadgeStyle(rfiInput, rfisList) {
  const match = findRfiCatalogEntry(rfiInput, rfisList);
  if (!match) return null;
  if (
    !match.rfi_color_code_bg ||
    !match.rfi_color_code_text ||
    !match.rfi_color_code_border
  ) {
    return null;
  }
  return {
    bgColor: match.rfi_color_code_bg,
    textColor: match.rfi_color_code_text,
    borderColor: match.rfi_color_code_border,
  };
}

export const SERVICE_CATEGORY_PILL_BASE_CLASS =
  "inline-flex items-center rounded px-2 py-1 text-xs font-medium ring-1 ring-inset";

/** Inline styles for service pills — matches Capabilities / CustomBadges ring effect. */
export function rfiBadgeStyleToPillStyle({ bgColor, textColor, borderColor }) {
  if (!bgColor || !textColor || !borderColor) return null;
  const border = String(borderColor);
  const isHex = border.startsWith("#");
  return {
    backgroundColor: bgColor,
    color: textColor,
    ...(isHex
      ? { boxShadow: `inset 0 0 0 1px ${border}1A` }
      : { "--tw-ring-color": rgbToRgba(border, 0.2) }),
  };
}
